import { useSuitabilityReports } from 'src/suitability-report/hooks/useSuitabilityReports';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { useDataExports } from 'src/suitability-report/hooks/useDataExports';
import { BackgroundProcess, FileData } from 'src/data-capture/hooks/file-uploads/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useMeetingNotes } from 'src/suitability-report/hooks/useMeetingNotes';

type GeneratedDocument = {
  id: string;
  clientId: string;
  displayName: string;
  createdAt: string;
  documentType: 'suitability-report' | 'meeting-agenda' | 'data-export' | 'meeting-note';
  fileId?: number;
  renameFileId?: number;
  file?: FileData;
  backgroundProcess?: BackgroundProcess;
};

type UseGeneratedDocumentsOptions = {
  clientId?: string;
};

export const useGeneratedDocuments = ({
  clientId: overrideClientId,
}: UseGeneratedDocumentsOptions = {}) => {
  const { clientId: defaultClientId } = useCurrentClient();

  const clientId = overrideClientId ?? defaultClientId;

  const {
    suitabilityReports,
    isLoading: suitabilityLoading,
    error: suitabilityError,
    refetch: refetchSuitability,
  } = useSuitabilityReports({ clientId });

  const {
    meetingAgendas,
    isLoading: agendasLoading,
    error: agendasError,
    refetch: refetchAgendas,
  } = useMeetingAgenda({ clientId });

  const {
    dataExports,
    isLoading: dataExportsLoading,
    error: dataExportsError,
    refetch: refetchDataExports,
  } = useDataExports();

  const {
    meetingNotes,
    isLoading: notesLoading,
    error: notesError,
    refetch: refetchNotes,
  } = useMeetingNotes();

  const documents: GeneratedDocument[] = [
    ...suitabilityReports.map(report => ({
      ...report,
      displayName: report.displayName || report.reportFile.fileName,
      documentType: 'suitability-report' as const,
      fileId: report.reportFileId,
      // Suitability reports are labelled by their template/type, not a file name,
      // so there is no file name for the user to rename here.
      renameFileId: undefined,
      file: report.reportFile,
      clientId: clientId,
    })),
    ...meetingAgendas.map(agenda => ({
      ...agenda,
      displayName: agenda.fileName || 'Meeting Agenda',
      documentType: 'meeting-agenda' as const,
      renameFileId: agenda.fileId,
      clientId: clientId,
    })),
    ...dataExports.map(dataExport => ({
      ...dataExport,
      displayName: dataExport.fileName || 'Data Export',
      documentType: 'data-export' as const,
      renameFileId: dataExport.fileId,
      clientId: clientId,
    })),
    ...meetingNotes.map(meetingNote => ({
      ...meetingNote,
      displayName: meetingNote.fileName || 'Meeting Note',
      documentType: 'meeting-note' as const,
      fileId: meetingNote.templatisedFileId,
      renameFileId: meetingNote.templatisedFileId,
      file: meetingNote.templatisedFile,
      clientId: clientId,
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const isLoading = suitabilityLoading || agendasLoading || dataExportsLoading || notesLoading;
  const error = (suitabilityError ??
    agendasError ??
    dataExportsError ??
    notesError) as Error | null;

  const refetch = async () => {
    await Promise.all([
      refetchSuitability(),
      refetchAgendas(),
      refetchDataExports(),
      refetchNotes(),
    ]);
  };

  return {
    documents,
    isLoading,
    error,
    refetch,
  };
};
